import { inject, Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { filter, first, firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { UserService } from './current-user.service';

/**
 * Routes on which a push tap must NOT navigate away: blocking screens the app
 * itself forces (maintenance / minimum version).
 */
const BLOCKING_ROUTES = ['/maintenance', '/version'];

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  /**
   * `registration` / `registrationError` listeners are registered ONCE per app
   * process. `initPush()` runs on every successful login; before this guard,
   * each call stacked another pair of listeners, so after N logins a single
   * token event wrote the token N times.
   */
  private registrationListenersAdded = false;

  /** `pushNotificationActionPerformed` listener registered (once per process). */
  private tapListenerAdded = false;

  /**
   * Asks for permission and registers the device for push, updating the
   * current user's `fcmToken`. Safe to call repeatedly (e.g. on every login):
   * listeners are added only once, while `register()` is invoked every time so
   * the native layer re-emits `registration` and the token is written for the
   * user that has just logged in.
   *
   * No-op on web.
   */
  initPush(): void {
    if (!Capacitor.isNativePlatform()) return;

    if (!this.registrationListenersAdded) {
      this.registrationListenersAdded = true;
      void PushNotifications.addListener('registration', (token: Token) => {
        void this.userService.updateUserToken(token.value);
      });
      void PushNotifications.addListener('registrationError', (error: unknown) => {
        console.error('[NotificationService] Push registration error', error);
      });
    }

    PushNotifications.requestPermissions()
      .then((permission) => {
        if (permission.receive === 'granted') {
          return PushNotifications.register();
        }
        return undefined;
      })
      .catch((error) => {
        console.error('[NotificationService] Push registration failed', error);
      });
  }

  /**
   * Opens `targetUrl` when the user taps a push notification.
   *
   * Must be called once at app startup (root component), NOT after login: on a
   * cold start (app killed, opened by tapping the push) the native plugin keeps
   * the tap event (`retainUntilConsumed`) and delivers it as soon as a listener
   * is added, which may happen before the router has completed its first
   * navigation and before Firebase Auth has resolved the session. The handler
   * therefore waits for both before navigating:
   *  - first completed navigation, so the tap lands on top of the normal start
   *    screen (back goes to it) and the splash/shell-guard flow is not cancelled;
   *  - resolved auth state: if there is a session it navigates; if there is no
   *    session the tap is dropped and the normal flow (home / login) continues.
   *
   * The route is a parameter because this service lives in `libs/services` and
   * the notifications screen only exists in apps/app.
   *
   * Idempotent and a no-op on web.
   */
  listenForNotificationTaps(targetUrl: string): void {
    if (!Capacitor.isNativePlatform() || this.tapListenerAdded) return;
    this.tapListenerAdded = true;

    void PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (_action: ActionPerformed) => {
        void this.openAfterTap(targetUrl);
      },
    );
  }

  private async openAfterTap(targetUrl: string): Promise<void> {
    try {
      if (!this.router.navigated) {
        await firstValueFrom(
          this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            first(),
          ),
        );
      }

      const loggedIn = await firstValueFrom(this.authService.isLoggedIn$);
      if (!loggedIn) return;

      const currentPath = this.router.url.split(/[?#]/)[0];
      if (currentPath === targetUrl) return;
      if (BLOCKING_ROUTES.some((route) => currentPath.startsWith(route))) return;

      // Capacitor listeners fire outside the Angular zone.
      await this.zone.run(() => this.router.navigateByUrl(targetUrl));
    } catch (error) {
      console.error('[NotificationService] Could not open notifications after push tap', error);
    }
  }
}
